import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';

dayjs.extend(relativeTime);
dayjs.extend(utc);

/**
 * Gets the user's timezone offset from localStorage security settings
 * @returns The timezone offset in hours, or null if not configured
 */
export const getUserTimezoneOffset = (): number | null => {
	try {
		const security = JSON.parse(localStorage.getItem('security') || '{}');
		const timeZone = security.timeZone;
		if (timeZone !== undefined && timeZone !== null && timeZone !== '') {
			return parseInt(timeZone, 10);
		}
	} catch (e) {
		console.error('Error parsing security from localStorage:', e);
	}
	return null;
};

/**
 * Formats a date string as relative time (e.g., "2 hours ago") considering user's timezone
 * @param dateString - The date string to format
 * @returns A human-readable relative time string
 */
export const formatRelativeTime = (dateString: string): string => {
	const userOffsetHours = getUserTimezoneOffset();

	if (userOffsetHours === null) {
		return dayjs(dateString).fromNow();
	}

	const apiDateAsUtc = dayjs.utc(dateString);
	const apiDateInRealUtc = apiDateAsUtc.subtract(userOffsetHours, 'hour');
	const nowUtc = dayjs.utc();

	return apiDateInRealUtc.from(nowUtc);
};

/**
 * Formats a date string that already comes adjusted to user's timezone from the API
 * @param dateString - The date string to format (already in user's timezone)
 * @param format - The dayjs format string (default: 'HH:mm DD-MMM-YYYY')
 * @returns A formatted date string
 */
export const formatDateWithTimezone = (dateString: string, format: string = 'HH:mm DD-MMM-YYYY'): string => {
	// The API already returns dates adjusted to the user's timezone,
	// so we just format it directly without any conversion
	return dayjs(dateString).format(format);
};
